import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, LabelList, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import { ChartContainer, type ChartConfig } from '../../elements/ui/chart';
import { ChartHoverCard } from '../../elements/ui/hover-card';
import { CHART_GROW_MS, useFirstDrawAnimation, useRevealOnScroll } from '../../hooks/useMotion';
import { busyDimClass } from '../../elements/ui/motion';
import { WeightBands, WeightMilestoneSeries, WeightMilestoneWeek } from '../../models/weight-milestones';
import { weightMilestoneColors as c } from './colors';

interface ChartRow extends WeightMilestoneWeek {
  label: string;
}

const num = (value: number) => value.toLocaleString();

const chartConfig: ChartConfig = {
  lowCount: { label: 'Low', color: c.band.Low },
  mediumCount: { label: 'Medium', color: c.band.Medium },
  highCount: { label: 'High', color: c.band.High }
};

/**
 * The three stacked segments, bottom to top. Lightest at the bottom so the stack reads the way the
 * scale does, and so the eye can compare the orange band across weeks along a common baseline - the
 * one segment of a stacked bar that is genuinely comparable.
 */
const SEGMENTS = [
  { band: 'Low', color: c.band.Low, labelKey: 'f-weights-band-low' },
  { band: 'Medium', color: c.band.Medium, labelKey: 'f-weights-band-medium' },
  { band: 'High', color: c.band.High, labelKey: 'f-weights-band-high' }
] as const;

/** How much of the band colour the back-calculated stack keeps, so it reads as the derived one. */
const CALCULATED_OPACITY = 0.4;

/**
 * The two stacks a week is drawn as, left to right: the weight as the farm recorded it, then the same
 * reading taken back to the day of birth. Two stack ids rather than one is what puts them beside each
 * other inside the week instead of on top of each other.
 *
 * They are always the same height, because they count the same piglets - the pair is read across, for
 * how the split moves between the bands. A piglet weighed two days late is recorded heavier than it
 * was born, so weeks weighed late lean green on the left and yellow on the right.
 */
const STACKS = [
  {
    stackId: 'recorded',
    fillOpacity: 1,
    labelKey: 'f-weights-column-recorded',
    legendKey: 'f-weights-legend-recorded',
    keys: { Low: 'lowCount', Medium: 'mediumCount', High: 'highCount' }
  },
  {
    stackId: 'calculated',
    fillOpacity: CALCULATED_OPACITY,
    labelKey: 'f-weights-column-calculated',
    legendKey: 'f-weights-legend-calculated',
    keys: { Low: 'calculatedLowCount', Medium: 'calculatedMediumCount', High: 'calculatedHighCount' }
  }
] as const;

/** Flat, because recharts looks for `Bar` among its immediate children. */
const BARS = STACKS.flatMap((stack) => SEGMENTS.map((segment, index) => ({ stack, segment, index })));

interface WeekTooltipProps {
  active?: boolean;
  payload?: { payload: ChartRow }[];
}

/** The bands top-down, matching how the stack is drawn rather than how SEGMENTS is declared. */
const TOOLTIP_SEGMENTS = [...SEGMENTS].reverse();

function WeekTooltip({ active, payload }: WeekTooltipProps) {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const week = payload[0].payload;
  const share = (count: number) => (week.measuredCount === 0 ? 0 : Math.round((count / week.measuredCount) * 100));
  const countWithShare = (count: number) =>
    t('f-weights-count-with-share', { count: num(count), percent: share(count) });
  const grams = (value: number | null) => (value === null ? '-' : t('f-weights-grams', { value: num(value) }));

  return (
    <ChartHoverCard className="min-w-72">
      <p className="mb-1.5 font-medium text-gray-700">{t('f-weights-tooltip-week', { week: week.weekNumber })}</p>

      {/* One grid for every row, so the two columns line up down the whole card. */}
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-1">
        <span />
        {STACKS.map((stack) => (
          <span key={stack.stackId} className="text-right text-[11px] uppercase tracking-wide text-gray-400">
            {t(stack.labelKey)}
          </span>
        ))}

        {/* Counts first, because the chart is a count; the share is the supporting figure. */}
        {TOOLTIP_SEGMENTS.map((segment) => (
          <Fragment key={segment.band}>
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: segment.color }} />
              {t(segment.labelKey)}
            </span>
            {STACKS.map((stack) => (
              <span
                key={stack.stackId}
                className="text-right font-medium tabular-nums text-gray-900"
                style={{ opacity: stack.fillOpacity === 1 ? 1 : 0.75 }}
              >
                {countWithShare(week[stack.keys[segment.band]])}
              </span>
            ))}
          </Fragment>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-2">
        <span className="text-gray-500">{t('f-weights-tooltip-measured')}</span>
        {/* One figure for both stacks: they count the same piglets. */}
        <span className="col-span-2 text-right font-medium tabular-nums text-gray-900">{num(week.measuredCount)}</span>

        <span className="text-gray-500">{t('f-weights-tooltip-average')}</span>
        <span className="text-right font-medium tabular-nums text-gray-900">{grams(week.averageGrams)}</span>
        <span className="text-right font-medium tabular-nums text-gray-600">{grams(week.calculatedAverageGrams)}</span>
      </div>
    </ChartHoverCard>
  );
}

/**
 * Legend is hand-rolled rather than taken from recharts: each entry names the gram range the API
 * split by, which the generated one has no way to know.
 */
function BandLegendRow({ bands }: { bands: WeightBands | null }) {
  const { t } = useTranslation();

  if (!bands) return null;

  const ranges: Record<string, string> = {
    'f-weights-band-low': t('f-weights-legend-low', { from: num(bands.mediumFromGrams) }),
    'f-weights-band-medium': t('f-weights-legend-medium', {
      from: num(bands.mediumFromGrams),
      to: num(bands.mediumToGrams)
    }),
    'f-weights-band-high': t('f-weights-legend-high', { to: num(bands.mediumToGrams) })
  };

  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
      {SEGMENTS.map((segment) => (
        <span key={segment.band} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: segment.color }} />
          {ranges[segment.labelKey]}
        </span>
      ))}
    </div>
  );
}

/**
 * Says which of a week's two bars is which. The pair carries no colour of its own - both are the band
 * colours - so the only thing to name is the fill: solid is what the farm weighed, faded is that
 * reading taken back to the day of birth.
 */
function StackLegendRow() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
      {STACKS.map((stack) => (
        <span key={stack.stackId} className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-[2px]"
            style={{ backgroundColor: c.band.Medium, opacity: stack.fillOpacity }}
          />
          {t(stack.legendKey)}
        </span>
      ))}
    </div>
  );
}

/**
 * Recharts' automatic domain produces ticks like 7 / 14 / 21 once a busy week stretches the axis.
 * Rounding the top up to a 1/2/2.5/5 x 10^n step gets back to countable gridlines.
 */
const buildYAxisScale = (maxValue: number): { max: number; ticks: number[] } => {
  if (maxValue <= 0) return { max: 4, ticks: [0, 2, 4] };

  const targetIntervals = 4;
  const rawStep = maxValue / targetIntervals;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= rawStep) ?? 10 * magnitude;
  const max = Math.ceil(maxValue / step) * step;

  const ticks: number[] = [];
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(Math.round(value));
  }

  return { max, ticks };
};

const ChartSkeleton = () => (
  <div className="flex h-full w-full items-end gap-2 px-4 pb-6">
    {[62, 70, 58, 74, 66, 78, 60, 68, 72, 64, 56, 70].map((height, i) => (
      <Skeleton key={i} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${height}%` }} />
    ))}
  </div>
);

export interface BirthWeightChartProps {
  series?: WeightMilestoneSeries | null;
  isBusy: boolean;
}

/**
 * Piglets weighed at birth per week, stacked by the band their weight fell in.
 *
 * Two bars per week, not one: the weight as the farm recorded it, and the same reading taken back to
 * the day of birth at 100 g a day - which is the birth weight the rest of the system stores and works
 * from. They only agree for a piglet weighed on the day it was born, and the gap crosses a band often
 * enough to matter: 1350 g on day two is heavy as recorded and on target at birth.
 *
 * Counts rather than percentages: a week in which two piglets were weighed and both came out light
 * is not the same story as a week in which forty were, and a 100% stack tells both the same way. The
 * bands themselves are backend configuration sent alongside the data, never numbers held here.
 *
 * A piglet is counted in the week it was **born**, not the day it happened to be weighed, so this chart
 * describes the same weeks as the registration chart above it. Bucketing by the reading's own date put
 * a batch weighed on a Monday into the following week and made this chart report more weights in a week
 * than the farm had registered piglets in it.
 */
export default function BirthWeightChart({ series, isBusy }: BirthWeightChartProps) {
  const { t } = useTranslation();

  const rows: ChartRow[] = (series?.weeks ?? []).map((week) => ({
    ...week,
    label: t('f-weights-week-short', { week: week.weekNumber })
  }));

  const bands = series?.bands ?? null;
  const yScale = buildYAxisScale(Math.max(0, ...rows.map((row) => row.measuredCount)));
  const hasMeasurements = rows.some((row) => row.measuredCount > 0);

  // Drawn once it scrolls into view, so the bars grow in where the reader can see them. A reload keeps the
  // previous chart on screen, dimmed, rather than dropping back to the skeleton.
  const { ref: plotRef, revealed } = useRevealOnScroll();
  const animateFirstDraw = useFirstDrawAnimation(revealed && hasMeasurements);

  return (
    <Card>
      <CardHeader className="space-y-1 pb-4">
        <h3 className="text-base font-semibold text-gray-900">{t('f-weights-chart-title')}</h3>
        <p className="text-xs text-gray-500">{t('f-weights-chart-subtitle')}</p>
        <div className="space-y-1.5 pt-2">
          <BandLegendRow bands={bands} />
          <StackLegendRow />
        </div>
      </CardHeader>

      <CardContent>
        <div ref={plotRef} className="h-80 w-full">
          {isBusy && !hasMeasurements ? (
            <ChartSkeleton />
          ) : !hasMeasurements ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">{t('f-weights-empty')}</div>
          ) : !revealed ? null : (
            <ChartContainer
              config={chartConfig}
              resizeDebounce={250}
              className={`h-full w-full ${busyDimClass(isBusy)}`}
            >
              {/* barGap: the two stacks of a week touch, so the week reads as one bar split in two
                  rather than as two neighbouring weeks. */}
              <BarChart data={rows} margin={{ top: 24, right: 24, bottom: 8, left: 0 }} maxBarSize={14} barGap={1}>
                <CartesianGrid vertical={false} stroke={c.grid} />

                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickMargin={8}
                />
                <YAxis
                  allowDecimals={false}
                  domain={[0, yScale.max]}
                  ticks={yScale.ticks}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                />

                {/* isAnimationActive: the wrapper the card measures itself against has to jump to the
                    cursor, not glide there. See ChartHoverCard. */}
                <RechartsTooltip cursor={false} isAnimationActive={false} content={<WeekTooltip />} />

                {BARS.map(({ stack, segment, index }) => (
                  <Bar
                    key={`${stack.stackId}-${segment.band}`}
                    dataKey={stack.keys[segment.band]}
                    stackId={stack.stackId}
                    fill={segment.color}
                    fillOpacity={stack.fillOpacity}
                    isAnimationActive={animateFirstDraw}
                    animationDuration={CHART_GROW_MS}
                    animationEasing="ease-out"
                    // Only the top segment gets the rounded cap, or every band in the stack looks
                    // like a bar of its own.
                    radius={index === SEGMENTS.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  >
                    {/* The total sits above the pair once. Both stacks are the same height, so it is
                        drawn on the recorded one and reads for both. */}
                    {index === SEGMENTS.length - 1 && stack.stackId === 'recorded' && (
                      <LabelList
                        dataKey="measuredCount"
                        position="top"
                        offset={8}
                        fill={c.ink}
                        fontSize={11}
                        fontWeight={600}
                        formatter={(value) => (Number(value) === 0 ? '' : num(Number(value)))}
                      />
                    )}
                  </Bar>
                ))}
              </BarChart>
            </ChartContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
